/**
 * InlineEditableCell
 *
 * Wraps a standard table cell to make it inline-editable.
 * - Default: renders display content (children) with hover affordance
 * - Active: renders DataGridCellRenderer with correct cell variant
 * - Checkbox: always interactive (no click-to-activate flow)
 * - Conflict: yellow ring + tooltip when another user modified the row
 *
 * Composes with useInlineTableEditing context for saves and navigation.
 * Wrapped with React.memo to prevent re-render storms in large tables.
 */

import React, { useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { resolveCellVariant } from '@/components/data-grid/dataGridCellResolver';
import { DataGridCellRenderer } from '@/components/data-grid/DataGridCellRenderer';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LinkFieldPopover } from './LinkFieldPopover';
import { SelectFieldPopover } from './SelectFieldPopover';
import type { InlineEditingContext } from './types';

interface InlineEditableCellProps {
  /** Frappe document name (row identifier) */
  rowName: string;
  /** Field name (column identifier) */
  fieldname: string;
  /** Current field value from row data */
  value: unknown;
  /** Inline editing context from useInlineTableEditing */
  context: InlineEditingContext;
  /** Display content (flexRender output) — shown when cell is not active */
  children: React.ReactNode;
}

/** Parse Select field options from Frappe meta (newline-separated string) */
function parseSelectOptions(raw?: string): { label: string; value: string }[] | undefined {
  if (!raw) return undefined;
  return raw
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => ({ label: s, value: s }));
}

function InlineEditableCellInner({
  rowName,
  fieldname,
  value,
  context,
  children,
}: InlineEditableCellProps) {
  // Refs for double-commit guard (G5, G16) and navigation intent (G15)
  const committingRef = useRef(false);
  const lastKeyRef = useRef<string | null>(null);

  // Resolve field metadata and cell variant
  const fieldMeta = context.fieldMetaMap.get(fieldname);
  const variant = resolveCellVariant(fieldMeta?.fieldtype, fieldname, context.editableFields);

  // Cell state
  const isActive = context.activeCell?.rowName === rowName
    && context.activeCell?.fieldname === fieldname;
  const saveState = context.getCellSaveState(rowName, fieldname);
  const isCheckbox = variant === 'checkbox';

  // Parse Select options at render time (G4)
  const selectOptions = variant === 'select' ? parseSelectOptions(fieldMeta?.options) : undefined;

  // --- Handlers ---

  // Single click: let it bubble to row (opens slide-over). Only handle error clearing.
  const handleClick = useCallback((_e: React.MouseEvent) => {
    if (isCheckbox) return;
    if (saveState.status === 'error' || saveState.conflict) {
      context.clearError(rowName, fieldname);
    }
    // Don't activate or stopPropagation — row click opens slide-over
  }, [isCheckbox, saveState.status, saveState.conflict, context, rowName, fieldname]);

  // Double-click: enter edit mode (Google Sheets style)
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // prevent row double-click side effects
    if (isCheckbox) return;
    context.startEditing(rowName, fieldname);
  }, [isCheckbox, context, rowName, fieldname]);

  const handleCommit = useCallback((newValue: unknown) => {
    // G5: Double-commit guard — skip if already committing
    if (committingRef.current) return;
    committingRef.current = true;

    // G16: Reset committingRef on next tick so future edits work
    setTimeout(() => { committingRef.current = false; }, 0);

    // Save the value
    context.saveCell({
      doctype: context.doctype,
      name: rowName,
      fieldname,
      value: newValue,
      oldValue: value,
    });

    // G15: Navigation based on which key triggered the commit
    const key = lastKeyRef.current;
    lastKeyRef.current = null;

    if (key === 'Enter') {
      context.moveDown(rowName, fieldname);
    } else if (key === 'Tab') {
      context.moveToNextCell(rowName, fieldname, 'forward');
    } else if (key === 'ShiftTab') {
      context.moveToNextCell(rowName, fieldname, 'backward');
    } else {
      // Blur without keypress — just deactivate
      context.deactivateCell();
    }
  }, [context, rowName, fieldname, value]);

  const handleCancel = useCallback(() => {
    // G13: If commit already fired (e.g., Select commit then Radix onOpenChange),
    // skip the cancel to avoid nulling activeCell after navigation
    if (committingRef.current) return;
    context.deactivateCell();
  }, [context]);

  const handleStartEditing = useCallback(() => {
    // No-op — editing starts when cell is activated
  }, []);

  // Track keyboard intent on the wrapper div (G15)
  // Tab doesn't stop propagation in DataGridCellText, so it bubbles here
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        lastKeyRef.current = 'ShiftTab';
      } else {
        lastKeyRef.current = 'Tab';
      }
    } else if (e.key === 'Enter') {
      lastKeyRef.current = 'Enter';
    } else if (e.key === 'Escape') {
      lastKeyRef.current = null;
    }
  }, []);

  // Popover commit handler — saves and deactivates (no Tab/Enter navigation)
  const handlePopoverCommit = useCallback((newValue: string) => {
    if (committingRef.current) return;
    committingRef.current = true;
    setTimeout(() => { committingRef.current = false; }, 0);

    context.saveCell({
      doctype: context.doctype,
      name: rowName,
      fieldname,
      value: newValue,
      oldValue: value,
    });
    context.deactivateCell();
  }, [context, rowName, fieldname, value]);

  // Checkbox commit handler — directly saves without going through active state
  const handleCheckboxCommit = useCallback((newValue: unknown) => {
    context.saveCell({
      doctype: context.doctype,
      name: rowName,
      fieldname,
      value: newValue,
      oldValue: value,
    });
  }, [context, rowName, fieldname, value]);

  // --- Render ---

  // Checkbox: always render the interactive checkbox (G3)
  if (isCheckbox) {
    return (
      <div
        className={cn(
          'relative',
          saveState.status === 'error' && 'ring-2 ring-red-500 rounded-sm',
          saveState.conflict && 'ring-2 ring-yellow-500 rounded-sm',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <DataGridCellRenderer
          variant="checkbox"
          value={value}
          onCommit={handleCheckboxCommit}
          onCancel={handleCancel}
          onStartEditing={handleStartEditing}
          saveState={saveState}
          fieldname={fieldname}
        />
      </div>
    );
  }

  // Active cell in editing mode: render the editor
  if (isActive && context.isEditing) {
    // Link fields → searchable popover (fetches via search_link)
    if (fieldMeta?.fieldtype === 'Link' && fieldMeta.options) {
      return (
        <div
          onClick={(e) => e.stopPropagation()}
        >
          <LinkFieldPopover
            doctype={fieldMeta.options}
            value={value}
            onCommit={handlePopoverCommit}
            onCancel={handleCancel}
            saveState={saveState}
          />
        </div>
      );
    }

    // Select fields → filterable popover (static options)
    if (fieldMeta?.fieldtype === 'Select' && fieldMeta.options) {
      return (
        <div
          onClick={(e) => e.stopPropagation()}
        >
          <SelectFieldPopover
            options={fieldMeta.options}
            value={value}
            onCommit={handlePopoverCommit}
            onCancel={handleCancel}
            saveState={saveState}
          />
        </div>
      );
    }

    // All other variants → existing DataGridCellRenderer
    return (
      <div
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <DataGridCellRenderer
          variant={variant}
          value={value}
          onCommit={handleCommit}
          onCancel={handleCancel}
          onStartEditing={handleStartEditing}
          saveState={saveState}
          fieldname={fieldname}
          options={selectOptions}
        />
      </div>
    );
  }

  // Default: display content with hover affordance + conflict/error states
  const hasConflict = saveState.conflict && saveState.status === 'idle';

  const cellContent = (
    <div
      className={cn(
        'relative transition-[box-shadow,border-color] duration-100',
        // Google Sheets style — no visible border, just cursor hint
        saveState.status !== 'error' && !hasConflict && [
          variant === 'select' ? 'cursor-pointer' : 'cursor-cell',
        ],
        // Error state — keep visible (loud failure)
        saveState.status === 'error' && 'ring-2 ring-red-500 rounded-sm cursor-pointer',
        // Conflict state — keep visible
        hasConflict && 'ring-2 ring-yellow-500 rounded-sm cursor-pointer',
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {children}
    </div>
  );

  // Wrap with tooltip when conflict is active
  if (hasConflict) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            {cellContent}
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Modified by another user
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cellContent;
}

/**
 * Memoized wrapper to prevent re-render storms when editing cells.
 *
 * The context object changes reference on every activeCell change (state update).
 * This comparator skips re-renders for cells that remain inactive with the same value,
 * avoiding O(n) re-renders across the entire table on each cell activation.
 */
export const InlineEditableCell = React.memo(InlineEditableCellInner, (prev, next) => {
  // Always re-render if identity or value changed
  if (prev.rowName !== next.rowName) return false;
  if (prev.fieldname !== next.fieldname) return false;
  if (prev.value !== next.value) return false;
  // Note: children from flexRender are always new elements (referential inequality).
  // We skip comparing children — value equality is sufficient since children
  // are a visual representation of the value.

  // Check if this cell's active state changed
  const wasActive = prev.context.activeCell?.rowName === prev.rowName
    && prev.context.activeCell?.fieldname === prev.fieldname;
  const isActive = next.context.activeCell?.rowName === next.rowName
    && next.context.activeCell?.fieldname === next.fieldname;
  if (wasActive !== isActive) return false;

  // If cell is active in both renders, always re-render (editor may have state changes)
  if (wasActive && isActive) return false;

  // If editing state changed, re-render active cells
  if (prev.context.isEditing !== next.context.isEditing && isActive) return false;

  // Cell is inactive in both renders with same value — skip re-render.
  // Context ref changes on every activeCell or forceUpdate (save state) change.
  // For inactive cells with unchanged value, save state visual transitions
  // (saving ring) resolve when query invalidation updates the value prop.
  return true;
});
